import { createEntityId } from '@/core/EntityId';
import { PRUNING_STYLES, type FloraInstance, type PruningStyle } from '@/flora/FloraInstance';
import { blockingObject, type SiteObject } from '@/site/SiteObject';
import { pointInRing, ringBounds, type Point2 } from '@/site/SiteFootprint';
import { sampleHeightWorld, type Heightfield } from '@/terrain/TerrainTypes';
import type { PlanPlanting } from './SitePlan';

/**
 * Expanding a planting **mass** into individual plants.
 *
 * This is the arithmetic half of the reconstruction feature, and the half a language model cannot
 * do: placing 190 plants that respect per-species spacing, dodge every building, sit exactly on the
 * heightfield, and reproduce byte-for-byte from the same seed is rejection sampling, not language.
 *
 * Its own module so the pipeline compiler and the magic wand share one implementation. Two callers
 * with two copies of a spacing rule is how a site that "works in the pipeline but not from the wand"
 * comes about.
 *
 * Fully deterministic: seeded RNG, no clock, no `Math.random`.
 */

/** Attempts per plant before a mass gives up on finding room. */
export const PLACEMENT_ATTEMPTS = 400;

export interface PlacedPlant {
  x: number;
  z: number;
  layer: string;
  minSpacing: number;
}

/**
 * Why one candidate position was refused.
 *
 * Tallied so a mass that placed nothing can say *what stopped it* rather than listing every reason
 * it might have been. "Only 0 of 9 fitted — the spot is crowded, off the site, blocked by a
 * building, or on ground that refuses planting" is four guesses; one of them is the answer, and the
 * engine is the only thing that knows which.
 */
export type PlacementRefusal =
  /** Outside the site's own footprint. */
  | 'off-site'
  /** Outside the mass's own boundary polygon — the shape the designer drew. */
  | 'outside-area'
  /** Inside a building's or paving's keep-out. */
  | 'structure'
  /** On ground that refuses planting — water, blacktop, paving. */
  | 'ground'
  /** Too close to another plant this same spell is placing, in the same layer. */
  | 'crowded'
  /** Inside a plant that was already on the site. */
  | 'established';

export interface PlacementContext {
  field: Heightfield;
  halfX: number;
  halfZ: number;
  siteObjects: readonly SiteObject[];
  placed: PlacedPlant[];
  rand: () => number;
  /**
   * Optional extra ground test — used by the pipeline to refuse planting on painted blacktop and
   * water, which is a rule the paint tool already enforces interactively. Absent means all ground
   * is plantable, which is the single-call plan's behaviour.
   */
  plantable?: (x: number, z: number) => boolean;
  /**
   * An extra keep-out the caller owns, tested against the plants that were **already on the site**.
   *
   * It is a hook rather than more entries in {@link placed} because the two are not the same kind of
   * fact. `placed` holds this spell's own masses, which carry a layer and a spacing the model chose;
   * an established plant has neither — it has a species, an age, and a crown. Pushing it into
   * `placed` means inventing a layer for it, and inventing the same one for every layer in the spell
   * makes one mature tree an exclusion zone for the entire garden, which is precisely how
   * underplanting became impossible.
   */
  occupied?: (
    x: number,
    z: number,
    planting: PlanPlanting,
    footprintRadiusMeters: number,
  ) => boolean;
  /** Told why each candidate was refused. See {@link PlacementRefusal}. */
  onRefusal?: (reason: PlacementRefusal) => void;
}

/** A candidate generator. Called repeatedly for a scattered mass; fixed for a line. */
export type Target = () => { x: number; z: number };

export interface MassTargets {
  targets: Target[];
  /**
   * Whether a rejected candidate can be re-drawn. A scattered mass can try elsewhere; a plant on a
   * line has exactly one place to be, and moving it would bend the hedge it belongs to.
   */
  retryable: boolean;
}

/**
 * Turn a planting mass into candidate positions.
 *
 * `line` wins over `drifts` over `polygon` over `regions` when a plan supplies more than one,
 * because a line is the most specific intent a model can express and silently blending two forms
 * would produce a mass that matches neither.
 *
 * `polygon` is the exception to that ordering: it is a **boundary**, not a form, so it also
 * constrains whichever other form is present (see {@link tryPlace}). It only generates candidates
 * of its own when nothing else does — which is the case that matters when the designer drew the
 * outline themselves and simply wants it filled.
 */
export function targetPositions(planting: PlanPlanting, context: PlacementContext): MassTargets {
  if (planting.line) {
    const { points, spacingMeters, offsetMeters = 0, jitterMeters = 0 } = planting.line;
    const marched = marchPolyline(points, spacingMeters, offsetMeters, jitterMeters, context);
    return { targets: marched.map((point) => () => point), retryable: false };
  }

  if (planting.drifts && planting.drifts.length > 0) {
    const targets: Target[] = [];
    for (const drift of planting.drifts) {
      for (let i = 0; i < drift.count; i += 1) {
        targets.push(() => {
          const angle = context.rand() * Math.PI * 2;
          const radius = drift.radiusMeters * Math.sqrt(context.rand());
          return { x: drift.x + Math.cos(angle) * radius, z: drift.z + Math.sin(angle) * radius };
        });
      }
    }
    return { targets, retryable: true };
  }

  // Rejection sampling over the outline's bounding box. The boundary test in `tryPlace` throws away
  // the candidates that land outside, so a concave shape — which is what a hand-drawn bed is —
  // fills correctly without anyone having to triangulate it.
  const boundary = boundaryRing(planting);
  if (boundary && planting.count && !planting.drifts && !planting.regions) {
    const bounds = ringBounds(boundary);
    const targets: Target[] = [];
    for (let i = 0; i < planting.count; i += 1) {
      targets.push(() => ({
        x: bounds.minX + context.rand() * (bounds.maxX - bounds.minX),
        z: bounds.minZ + context.rand() * (bounds.maxZ - bounds.minZ),
      }));
    }
    return { targets, retryable: true };
  }

  if (planting.regions && planting.regions.length > 0 && planting.count) {
    const regions = planting.regions;
    const areas = regions.map((r) => Math.max(0, (r.x1 - r.x0) * (r.z1 - r.z0)));
    const total = areas.reduce((a, b) => a + b, 0);
    if (total <= 0) return { targets: [], retryable: true };

    const targets: Target[] = [];
    for (let i = 0; i < planting.count; i += 1) {
      targets.push(() => {
        // Area-weighted, so a wide band is not undersampled by a narrow one.
        let pick = context.rand() * total;
        let chosen = regions[regions.length - 1] as (typeof regions)[number];
        for (let r = 0; r < regions.length; r += 1) {
          pick -= areas[r] as number;
          if (pick <= 0) {
            chosen = regions[r] as (typeof regions)[number];
            break;
          }
        }
        return {
          x: chosen.x0 + context.rand() * (chosen.x1 - chosen.x0),
          z: chosen.z0 + context.rand() * (chosen.z1 - chosen.z0),
        };
      });
    }
    return { targets, retryable: true };
  }

  return { targets: [], retryable: true };
}

/** Points marching along a polyline, offset sideways and jittered. */
export function marchPolyline(
  points: readonly [number, number][],
  spacing: number,
  offset: number,
  jitter: number,
  context: PlacementContext,
): { x: number; z: number }[] {
  const out: { x: number; z: number }[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const [x0, z0] = points[i] as [number, number];
    const [x1, z1] = points[i + 1] as [number, number];
    const dx = x1 - x0;
    const dz = z1 - z0;
    const length = Math.hypot(dx, dz);
    if (length < 1e-6) continue;
    const steps = Math.max(1, Math.round(length / spacing));
    const nx = -dz / length;
    const nz = dx / length;
    // The last point of a segment is the first of the next; emit it only on the final segment.
    const last = i === points.length - 2 ? steps : steps - 1;
    for (let s = 0; s <= last; s += 1) {
      const t = s / steps;
      out.push({
        x: x0 + dx * t + nx * offset + (context.rand() * 2 - 1) * jitter,
        z: z0 + dz * t + nz * offset + (context.rand() * 2 - 1) * jitter,
      });
    }
  }
  return out;
}

/**
 * Try a candidate, re-drawing a scattered one until it finds room. Returns null when it cannot.
 *
 * Six independent reasons to refuse a point: it falls off the site, it falls outside the mass's own
 * boundary, a building is already there, the ground under it refuses planting, a neighbour from
 * this same spell is too close in the same layer, or a plant that was already on the site is
 * standing in it. Layers are checked separately by design — shrubs belong under trees — and each
 * refusal is reported so the caller can say which one actually stopped the mass.
 */
export function tryPlace(
  planting: PlanPlanting,
  target: Target,
  retryable: boolean,
  context: PlacementContext,
  footprintRadius: number,
  links: { groupId?: string; aliasGroupId?: string },
): FloraInstance | null {
  const margin = footprintRadius * 0.35;
  const attempts = retryable ? PLACEMENT_ATTEMPTS : 1;
  const boundary = boundaryRing(planting);
  const refuse = context.onRefusal;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const { x, z } = target();
    const insideSite = Math.abs(x) <= context.halfX - margin && Math.abs(z) <= context.halfZ - margin;
    if (!insideSite) {
      refuse?.('off-site');
      continue;
    }
    if (boundary && !pointInRing(boundary, x, z)) {
      refuse?.('outside-area');
      continue;
    }
    if (blockingObject(context.siteObjects, x, z) !== undefined) {
      refuse?.('structure');
      continue;
    }
    if (context.plantable && !context.plantable(x, z)) {
      refuse?.('ground');
      continue;
    }
    if (tooClose(context, x, z, planting.layer, planting.minSpacingMeters)) {
      refuse?.('crowded');
      continue;
    }
    if (context.occupied?.(x, z, planting, footprintRadius)) {
      refuse?.('established');
      continue;
    }

    context.placed.push({ x, z, layer: planting.layer, minSpacing: planting.minSpacingMeters });
    return instanceAt(planting, x, z, context, links);
  }
  return null;
}

/** The mass's boundary as a ring, or null when it has none. */
function boundaryRing(planting: PlanPlanting): Point2[] | null {
  const polygon = planting.polygon;
  if (!polygon || polygon.length < 3) return null;
  return polygon.map(([x, z]) => ({ x, z }));
}

export function instanceAt(
  planting: PlanPlanting,
  x: number,
  z: number,
  context: PlacementContext,
  links: { groupId?: string; aliasGroupId?: string },
): FloraInstance {
  const [low, high] = planting.maturity;
  const linked = planting.linked === true;
  const pruningStyle: PruningStyle = PRUNING_STYLES.includes(planting.pruningStyle as PruningStyle)
    ? (planting.pruningStyle as PruningStyle)
    : 'natural';

  const instance: FloraInstance = {
    id: createEntityId('flora-'),
    floraDefinitionId: planting.speciesId,
    position: { x: round(x), y: round(sampleHeightWorld(context.field, x, z)), z: round(z) },
    rotation: { x: 0, y: round(context.rand() * Math.PI * 2, 4), z: 0 },
    // A linked mass is one repeated plant, so its members must not drift apart.
    maturity: round(linked ? (low + high) / 2 : low + context.rand() * (high - low), 3),
    visualSeed: Math.floor(context.rand() * 0x7ffffff),
    health: 1,
    fullness: linked ? 1 : round(0.88 + context.rand() * 0.12, 3),
    seasonalOffsetDays: 0,
    sizeVariation: linked ? 0 : round((context.rand() * 2 - 1) * 0.14, 3),
    colorVariation: linked ? 0 : round((context.rand() * 2 - 1) * 0.12, 3),
    pruningStyle,
    locked: false,
    hidden: false,
  };
  if (links.groupId) instance.groupId = links.groupId;
  if (links.aliasGroupId) instance.aliasGroupId = links.aliasGroupId;
  return instance;
}

export function tooClose(
  context: PlacementContext,
  x: number,
  z: number,
  layer: string,
  minSpacing: number,
): boolean {
  for (const other of context.placed) {
    if (other.layer !== layer) continue;
    const need = Math.max(other.minSpacing, minSpacing);
    if ((other.x - x) ** 2 + (other.z - z) ** 2 < need * need) return true;
  }
  return false;
}

/** Small, fast, seeded PRNG. Deterministic across machines — no `Math.random` anywhere. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function round(value: number, places = 3): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}
